import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { STRINGS, isLanguage, legalLanguage, type Language } from './i18n';
import { getLastUpdatedDate, getSupportEmail } from './legalInfoUtils';

// Язык страницы — любой из 13 (шапка, подвал, строка-пояснение); сам документ — на английском, русском или испанском (legalLanguage), 2026-10-24.

// 2026-10-17 (аудит сторов С11): §5–6 поправлены (покупки — кристаллы и наборы ингредиентов; сброс сохраняет кристаллы, подарки и
// список покупок; перенос своего прогресса есть), добавлен §7 «Реклама». Прежняя версия — backlog/store-audit-wrapper-site-2026-10-17/.
const translations = {
  en: {
    title: 'Terms of Service',
    lastUpdated: `Last updated: ${getLastUpdatedDate('en')}`,
    sections: [
      {
        title: '1. Acceptance of Terms',
        content:
          'By downloading, installing, or using Qevryno ("the App"), you agree to these Terms of Service. If you do not agree with these Terms, please do not use the App.',
      },
      {
        title: '2. License',
        content:
          'We grant you a personal, non-exclusive, non-transferable, revocable license to use the App solely for personal entertainment purposes. You may not copy, modify, distribute, sell, sublicense, reverse engineer, decompile, or otherwise exploit the App or its assets except where permitted by applicable law.',
      },
      {
        title: '3. Platform Terms',
        content:
          'If you download the App through the Apple App Store, Apple’s Standard End User License Agreement (EULA) also applies (https://www.apple.com/legal/internet-services/itunes/dev/stdeula/). If you download the App through Google Play, your use of the App is also subject to Google’s applicable terms and policies. These Terms supplement, but do not replace, the applicable platform terms. Apple and Google are not responsible for providing support, maintenance, or content for the App except where required by applicable law.',
      },
      {
        title: '4. Fictional Content',
        content:
          'The App is a work of fiction created for entertainment purposes. All potions, recipes, ingredients, brewing methods, magical effects, characters, creatures, locations, and events depicted in the App are entirely fictional. Nothing in the App should be interpreted as real-world instructions, medical advice, health guidance, cosmetic advice, herbal recommendations, or professional guidance of any kind. Some real-world plants, mushrooms, and other natural materials may be dangerous or poisonous. Never use the App as a guide for identifying, collecting, preparing, or consuming them. We are not responsible for any real-world actions based on fictional content presented in the App.',
      },
      {
        title: '5. Virtual Currency and Virtual Items',
        content:
          'The App contains virtual currency and virtual items. They have no real-world monetary value, cannot be exchanged for real money, cannot be sold or transferred to other players, and are licensed to you rather than sold. You can move your own progress, including purchased items, between your devices with a save code or a save file (Settings → Backup). Virtual currency can be earned in the game or obtained through optional in-app purchases. We may modify, rebalance, replace, discontinue, or remove virtual currency, virtual items, or other game content as the App evolves. Promo codes, if offered, are promotional, may be limited or withdrawn at any time, and have no cash value.',
      },
      {
        title: '6. In-App Purchases and Refunds',
        content:
          'The App offers optional in-app purchases. At the time these Terms were last updated, they are consumable gem packs and ingredient packs; the contents of an ingredient pack are shown before you buy. Payments are processed exclusively by the Apple App Store or Google Play; we do not process payments and do not receive or store your payment information. The exact price is always shown by the app store before you confirm the purchase. Refunds are handled only by the platform where the purchase was made, under its own policies. The App has no player accounts or cloud sync and stores gameplay data on your device. Resetting progress in Settings keeps your gems, unopened gift boxes and the record of purchases already granted. Deleting the App removes all data stored on your device, including purchased items, unless you keep a save code, a save file or a device backup.',
      },
      {
        title: '7. Advertising',
        content:
          'Optional rewarded videos are provided by Google AdMob. A reward is given only when you watch a video to the end. Availability depends on your network and region, and daily limits apply. Rewards from videos have no cash value. Nothing in the game requires watching ads.',
      },
      {
        title: '8. Updates, Availability, and Changes to these Terms',
        content:
          'We may update, modify, suspend, discontinue, or expand features, content, or services at any time, including by introducing new types of in-app purchases, advertising, or online services in future versions. We may also update these Terms from time to time. The latest version will always be available on our official Terms of Service page and will display the “Last updated” date. By continuing to use the App after changes become effective, you agree to the updated Terms. Availability of the App may depend on your device, operating system version, internet availability where required, and applicable platform policies.',
      },
      {
        title: '9. Disclaimer of Warranties',
        content:
          'The App is provided “as is” and “as available”, without warranties of any kind, whether express or implied. To the maximum extent permitted by applicable law, we disclaim all warranties, including warranties of merchantability, fitness for a particular purpose, and non-infringement.',
      },
      {
        title: '10. Limitation of Liability',
        content:
          'To the maximum extent permitted by applicable law, we shall not be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages arising from or related to your use of the App, including, without limitation, loss of gameplay progress, virtual currency, virtual items, or locally stored data.',
      },
      {
        title: '11. Contact',
        content: `If you have questions about these Terms, contact us at ${getSupportEmail()}.`,
      },
    ],
  },
  ru: {
    title: 'Условия использования',
    lastUpdated: `Последнее обновление: ${getLastUpdatedDate('ru')}`,
    sections: [
      {
        title: '1. Принятие условий',
        content:
          'Скачивая, устанавливая или используя Qevryno («приложение»), вы соглашаетесь с этими Условиями использования. Если вы не согласны — не используйте приложение.',
      },
      {
        title: '2. Лицензия',
        content:
          'Мы предоставляем вам личную, неисключительную, непередаваемую и отзывную лицензию на использование приложения исключительно в личных развлекательных целях. Нельзя копировать, модифицировать, распространять, продавать, сублицензировать, подвергать обратной разработке, декомпилировать или иным образом использовать приложение и его материалы, если иное не разрешено применимым законом.',
      },
      {
        title: '3. Условия платформ',
        content:
          'Если вы скачали приложение из Apple App Store, дополнительно действует Стандартная EULA Apple (https://www.apple.com/legal/internet-services/itunes/dev/stdeula/). Если вы скачали приложение из Google Play, дополнительно действуют применимые условия и политики Google. Эти Условия дополняют, но не заменяют условия платформ. Apple и Google не обязаны предоставлять поддержку, обслуживание или контент приложения, за исключением случаев, предусмотренных применимым законом.',
      },
      {
        title: '4. Вымышленное содержание',
        content:
          'Приложение — художественное произведение, созданное для развлечения. Все зелья, рецепты, ингредиенты, способы варки, магические эффекты, персонажи, существа, локации и события в приложении полностью вымышлены. Ничто в приложении не должно восприниматься как реальные инструкции, медицинский, оздоровительный, косметический или травнический совет либо профессиональная рекомендация любого рода. Некоторые настоящие растения, грибы и другие природные материалы могут быть опасны или ядовиты. Никогда не используйте приложение как руководство по их определению, сбору, приготовлению или употреблению. Мы не несём ответственности за любые действия в реальном мире, основанные на вымышленном содержании приложения.',
      },
      {
        title: '5. Виртуальная валюта и виртуальные предметы',
        content:
          'В приложении есть виртуальная валюта и виртуальные предметы. Они не имеют реальной денежной стоимости, не обмениваются на реальные деньги, их нельзя продать или передать другим игрокам; они предоставляются по лицензии, а не продаются. Свой прогресс, включая купленное, можно перенести между своими устройствами кодом или файлом сохранения («Настройки → Резервная копия»). Виртуальную валюту можно заработать в игре или получить через необязательные встроенные покупки. Мы можем изменять, ребалансировать, заменять, прекращать поддержку или удалять виртуальную валюту, виртуальные предметы и другой игровой контент по мере развития приложения. Промокоды, если мы их выпускаем, — рекламная акция: их действие может быть ограничено или прекращено в любой момент, денежной ценности они не имеют.',
      },
      {
        title: '6. Встроенные покупки и возвраты',
        content:
          'В приложении есть необязательные встроенные покупки. На момент последнего обновления этих Условий это расходуемые наборы кристаллов и наборы ингредиентов; состав набора ингредиентов виден до покупки. Платежи обрабатываются исключительно Apple App Store или Google Play; мы не обрабатываем платежи и не получаем и не храним ваши платёжные данные. Точная цена всегда показывается магазином приложений перед подтверждением покупки. Возвраты рассматривает только платформа, через которую совершена покупка, по своим правилам. Аккаунтов игроков и облачной синхронизации нет, игровые данные хранятся на вашем устройстве. Сброс прогресса в настройках оставляет кристаллы, неоткрытые подарки и список уже начисленных покупок. Удаление приложения стирает все данные на устройстве, включая купленное, если вы не сохранили код или файл сохранения либо резервную копию устройства.',
      },
      {
        title: '7. Реклама',
        content:
          'Необязательные ролики за награду показывает Google AdMob. Награда выдаётся только за ролик, досмотренный до конца. Доступность зависит от сети и региона, действуют дневные ограничения. Награды за ролики не имеют денежной стоимости. Ничто в игре не требует смотреть рекламу.',
      },
      {
        title: '8. Обновления, доступность и изменение Условий',
        content:
          'Мы можем в любой момент обновлять, изменять, приостанавливать, прекращать или расширять функции, контент и сервисы, в том числе добавлять в будущих версиях новые типы встроенных покупок, рекламу или онлайн-сервисы. Мы также можем время от времени обновлять эти Условия. Актуальная версия всегда доступна на официальной странице Условий с датой «Последнее обновление»; продолжая использовать приложение после вступления изменений в силу, вы соглашаетесь с обновлёнными Условиями. Доступность приложения может зависеть от устройства, версии ОС, наличия интернета (где он требуется) и правил платформ.',
      },
      {
        title: '9. Отказ от гарантий',
        content:
          'Приложение предоставляется «как есть» и «как доступно», без каких-либо гарантий, явных или подразумеваемых. В максимальной степени, разрешённой применимым законом, мы отказываемся от всех гарантий, включая гарантии товарной пригодности, соответствия определённой цели и ненарушения прав.',
      },
      {
        title: '10. Ограничение ответственности',
        content:
          'В максимальной степени, разрешённой применимым законом, мы не несём ответственности за любые косвенные, случайные, особые, последующие, показательные или штрафные убытки, возникающие из использования приложения или в связи с ним, включая, помимо прочего, утрату игрового прогресса, виртуальной валюты, виртуальных предметов или локально сохранённых данных.',
      },
      {
        title: '11. Связь',
        content: `По вопросам этих Условий пишите на ${getSupportEmail()}.`,
      },
    ],
  },
  es: {
    title: 'Términos de servicio',
    lastUpdated: `Última actualización: ${getLastUpdatedDate('es')}`,
    sections: [
      {
        title: '1. Aceptación de los Términos',
        content:
          'Al descargar, instalar o usar Qevryno («la App»), acepta estos Términos de servicio. Si no está de acuerdo con estos Términos, no use la App.',
      },
      {
        title: '2. Licencia',
        content:
          'Le concedemos una licencia personal, no exclusiva, no transferible y revocable para usar la App únicamente con fines de entretenimiento personal. No puede copiar, modificar, distribuir, vender, sublicenciar, realizar ingeniería inversa, descompilar ni explotar de otro modo la App o sus recursos, salvo lo permitido por la ley aplicable.',
      },
      {
        title: '3. Términos de las plataformas',
        content:
          'Si descarga la App desde Apple App Store, también se aplica la EULA estándar de Apple (https://www.apple.com/legal/internet-services/itunes/dev/stdeula/). Si la descarga desde Google Play, su uso también está sujeto a los términos y políticas aplicables de Google. Estos Términos complementan, pero no sustituyen, los términos de las plataformas. Apple y Google no son responsables de proporcionar soporte, mantenimiento ni contenido para la App, salvo cuando lo exija la ley aplicable.',
      },
      {
        title: '4. Contenido ficticio',
        content:
          'La App es una obra de ficción creada con fines de entretenimiento. Todas las pociones, recetas, ingredientes, métodos de elaboración, efectos mágicos, personajes, criaturas, lugares y eventos representados en la App son completamente ficticios. Nada en la App debe interpretarse como instrucciones del mundo real, consejo médico, orientación de salud, consejo cosmético, recomendaciones herbolarias ni orientación profesional de ningún tipo. Algunas plantas, setas y otros materiales naturales reales pueden ser peligrosos o venenosos. Nunca use la App como guía para identificarlos, recolectarlos, prepararlos o consumirlos. No somos responsables de ninguna acción en el mundo real basada en el contenido ficticio de la App.',
      },
      {
        title: '5. Moneda y artículos virtuales',
        content:
          'La App contiene moneda virtual y artículos virtuales. No tienen valor monetario real, no se pueden cambiar por dinero real, no se pueden vender ni ceder a otros jugadores; se le conceden bajo licencia, no se venden. Puede trasladar su propio progreso, incluido lo comprado, entre sus dispositivos con un código o un archivo de guardado (Ajustes → Copia de seguridad). La moneda virtual puede obtenerse jugando o mediante compras opcionales dentro de la app. Podemos modificar, reequilibrar, sustituir, descontinuar o retirar la moneda virtual, los artículos virtuales u otro contenido del juego a medida que la App evolucione. Los códigos promocionales, si se ofrecen, son promocionales, pueden limitarse o retirarse en cualquier momento y no tienen valor monetario.',
      },
      {
        title: '6. Compras dentro de la app y reembolsos',
        content:
          'La App ofrece compras opcionales dentro de la app. En el momento de la última actualización de estos Términos, son paquetes consumibles de gemas y paquetes de ingredientes; el contenido de un paquete de ingredientes se muestra antes de comprar. Los pagos se procesan exclusivamente por Apple App Store o Google Play; no procesamos pagos y no recibimos ni almacenamos sus datos de pago. El precio exacto siempre lo muestra la tienda de aplicaciones antes de confirmar la compra. Los reembolsos los gestiona únicamente la plataforma donde se realizó la compra, según sus propias políticas. La App no tiene cuentas de jugador ni sincronización en la nube y guarda los datos del juego en su dispositivo. Reiniciar el progreso en Ajustes conserva las gemas, los regalos sin abrir y el registro de compras ya concedidas. Eliminar la App borra todos los datos guardados en el dispositivo, incluido lo comprado, salvo que conserve un código o un archivo de guardado o una copia de seguridad del dispositivo.',
      },
      {
        title: '7. Publicidad',
        content:
          'Los vídeos opcionales con recompensa los ofrece Google AdMob. La recompensa se concede solo si ve el vídeo hasta el final. La disponibilidad depende de su conexión y región, y hay límites diarios. Las recompensas por vídeos no tienen valor monetario. Nada en el juego obliga a ver anuncios.',
      },
      {
        title: '8. Actualizaciones, disponibilidad y cambios en estos Términos',
        content:
          'Podemos actualizar, modificar, suspender, retirar o ampliar funciones, contenido o servicios en cualquier momento, incluida la introducción en futuras versiones de nuevos tipos de compras dentro de la app, publicidad u otros servicios en línea. También podemos actualizar estos Términos periódicamente. La versión más reciente estará siempre disponible en nuestra página oficial de Términos de servicio con su fecha de «Última actualización». Al continuar usando la App después de que los cambios entren en vigor, acepta los Términos actualizados. La disponibilidad de la App puede depender del dispositivo, la versión del sistema operativo, la conexión a internet cuando sea necesaria y las políticas de las plataformas.',
      },
      {
        title: '9. Exención de garantías',
        content:
          'La App se proporciona «tal cual» y «según disponibilidad», sin garantías de ningún tipo, expresas o implícitas. En la máxima medida permitida por la ley aplicable, renunciamos a todas las garantías, incluidas las de comerciabilidad, idoneidad para un fin determinado y no infracción.',
      },
      {
        title: '10. Limitación de responsabilidad',
        content:
          'En la máxima medida permitida por la ley aplicable, no seremos responsables de daños indirectos, incidentales, especiales, consecuentes, ejemplares o punitivos derivados del uso de la App o relacionados con él, incluida, entre otros, la pérdida de progreso del juego, moneda virtual, artículos virtuales o datos almacenados localmente.',
      },
      {
        title: '11. Contacto',
        content: `Si tiene preguntas sobre estos Términos, contáctenos en ${getSupportEmail()}.`,
      },
    ],
  },
};

export const Terms: React.FC = () => {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const [currentLang, setCurrentLang] = useState<Language>(isLanguage(lang) ? lang : 'ru');

  useEffect(() => {
    if (isLanguage(lang)) {
      setCurrentLang(lang);
    }
  }, [lang]);

  const docLang = legalLanguage(currentLang);
  const t = translations[docLang];
  const onlyIn = docLang === currentLang ? '' : STRINGS[currentLang].legal.onlyIn;

  return (
    <div
      className="landing"
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at 15% 20%, var(--color-rgba-59-130-246-16), var(--color-transparent) 35%), radial-gradient(circle at 85% 10%, var(--color-rgba-124-58-237-12), var(--color-transparent) 30%), var(--background)',
        color: 'var(--text)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
      }}
    >
      <Header language={currentLang} onLanguageChange={(newLang) => {
        setCurrentLang(newLang);
        navigate(`/terms/${newLang}`);
      }} />

      <div
        style={{
          flex: 1,
          padding: 'clamp(2.5rem, 5vw, 4rem) clamp(1.4rem, 4vw, 3rem) clamp(3rem, 5vw, 5rem)',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, var(--color-rgba-59-130-246-8), var(--color-rgba-17-24-39-35))',
            opacity: 0.9,
            pointerEvents: 'none',
          }}
        />
        <div style={{ maxWidth: '56rem', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <h1
            style={{
              fontSize: 'var(--font-h1)',
              margin: '0 0 0.5rem',
              lineHeight: 1.1,
              background: 'linear-gradient(120deg, var(--text) 0%, var(--primary) 70%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'var(--color-transparent)',
              backgroundClip: 'text',
            }}
          >
            {t.title}
          </h1>
          {onlyIn && (
            <p lang={currentLang} style={{ margin: '0 0 1rem', fontSize: 'var(--text-small)', color: 'var(--textSecondary)' }}>{onlyIn}</p>
          )}
          <p style={{ margin: '0 0 2rem', fontSize: 'var(--text-small)', color: 'var(--textTertiary)' }}>
            {t.lastUpdated}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {t.sections.map((section, index) => (
              <div
                key={index}
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '1.2rem',
                  padding: '1.5rem',
                }}
              >
                <h2
                  style={{
                    fontSize: 'var(--font-h2)',
                    fontWeight: 700,
                    margin: '0 0 0.75rem',
                    color: 'var(--text)',
                  }}
                >
                  {section.title}
                </h2>
                <p
                  style={{
                    margin: 0,
                    fontSize: 'var(--text-body)',
                    color: 'var(--textSecondary)',
                    lineHeight: 1.6,
                  }}
                >
                  {section.content}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer language={currentLang} />
    </div>
  );
};
